/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'

// Bootstrap-compatible FormGroup component
export interface FormGroupProps {
  controlId?: string
  className?: string
  children: React.ReactNode
}

export const FormGroup: React.FC<FormGroupProps> = ({ controlId, className = '', children }) => (
  <div className={`form-group ${className}`} id={controlId}>
    {children}
  </div>
)

// Bootstrap-compatible ControlLabel component
export interface ControlLabelProps {
  style?: React.CSSProperties
  className?: string
  htmlFor?: string
  children: React.ReactNode
}

export const ControlLabel: React.FC<ControlLabelProps> = ({ style, className = '', htmlFor, children }) => (
  <label style={style} className={`control-label ${className}`} htmlFor={htmlFor}>
    {children}
  </label>
)

// Bootstrap-compatible Button component
export interface ButtonProps {
  'data-action'?: string
  className?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  id?: string
  disabled?: boolean
  bsStyle?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'link'
  type?: 'button' | 'submit' | 'reset'
  children: React.ReactNode
}

export const Button: React.FC<ButtonProps> = ({
  className = '',
  onClick,
  children,
  disabled,
  id,
  bsStyle,
  type = 'button',
  ...props
}) => {
  // Map bsStyle to Bootstrap classes
  const getButtonClass = () => {
    let btnClass = 'btn'
    if (bsStyle) {
      btnClass += ` btn-${bsStyle}`
    }
    return `${btnClass} ${className}`.trim()
  }

  return (
    <button className={getButtonClass()} onClick={onClick} disabled={disabled} id={id} type={type} {...props}>
      {children}
    </button>
  )
}

// Bootstrap-compatible Collapse component
export interface CollapseProps {
  in: boolean
  className?: string
  children: React.ReactNode
}

export const Collapse: React.FC<CollapseProps> = ({ in: show, children, className = '' }) => (
  <div
    className={`collapse ${show ? 'in' : ''} ${className}`}
    style={{
      display: show ? 'block' : 'none',
      transition: 'height 0.35s ease'
    }}
  >
    {children}
  </div>
)
